import { LoadingButton } from "@mui/lab";
import { PageContainer } from "@/features/ui";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { useAuth } from "@/features/authentication";
import { OutletChartOfAccountForm } from "@/features/outlet-chart-of-accounts";
import { apiOutletClient, getErrorMessage } from "@/features/shared";
import { CenteredLoading, useSnackbar } from "@/features/ui";

export const EditOutletChartOfAccount = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { showSnackbar } = useSnackbar();
  const { outlet } = useAuth();
  const [isLoading, setIsLoading] = useState(true);
  const [defaultValues, setDefaultValues] = useState(null);

  useEffect(() => {
    const fetchOutletChartOfAccount = async () => {
      if (!outlet) return;

      setIsLoading(true);
      try {
        const {
          data: { data },
        } = await apiOutletClient.get(`chart-of-accounts/${id}`, {
          headers: {
            "Outlet-Id": outlet.id,
          },
          params: {
            withParent: true,
            withInitialBalanceTransaction: true,
          },
        });

        const {
          id: accountId,
          code,
          name,
          parent: parentChartOfAccount,
          type,
          deactivatedAt,
          initialBalanceTransaction,
        } = data;

        const shouldHaveParent = Boolean(parentChartOfAccount);

        setDefaultValues({
          id: accountId,
          code,
          name,
          hasParent: shouldHaveParent,
          parentChartOfAccount: shouldHaveParent ? parentChartOfAccount : null,
          type,
          isActive: !deactivatedAt,
          initialBalance: initialBalanceTransaction?.amount ?? null,
          initialBalanceEffectiveDate: initialBalanceTransaction?.transactionDate
            ? dayjs(initialBalanceTransaction.transactionDate)
            : null,
        });
      } catch {
        showSnackbar("Gagal mengambil data akun");
        navigate("/general-ledger/outlet-chart-of-accounts");
      } finally {
        setIsLoading(false);
      }
    };

    fetchOutletChartOfAccount();
  }, [id, outlet, navigate, showSnackbar]);

  const handleSubmit = async (data) => {
    if (!outlet) {
      showSnackbar("Outlet tidak ditemukan");
      return;
    }

    try {
      await apiOutletClient.put(`chart-of-accounts/${id}`, data, {
        headers: {
          "Outlet-Id": outlet.id,
        },
      });

      showSnackbar("Akun berhasil diperbarui", "success");
      navigate("/general-ledger/outlet-chart-of-accounts");
    } catch (error) {
      showSnackbar(getErrorMessage(error, "Gagal memperbarui akun"));

      throw error;
    }
  };

  return (
    <PageContainer
      maxWidth="xl"
      title="Ubah Akun Perkiraan Outlet"
      breadcrumbs={[
        {
          title: "Akun Outlet",
          path: "/general-ledger/outlet-chart-of-accounts",
        },
        { title: "Ubah Akun" },
      ]}
    >
      {isLoading ? (
        <CenteredLoading />
      ) : (
        outlet && (
          <OutletChartOfAccountForm
            outletId={outlet.id}
            defaultValues={defaultValues}
            onSubmit={handleSubmit}
            renderActionButton={({ isSubmitting }) => (
              <LoadingButton
                loading={isSubmitting}
                variant="contained"
                type="submit"
              >
                Simpan
              </LoadingButton>
            )}
          />
        )
      )}
    </PageContainer>
  );
};
